import { Track } from "@/src/lib/tracking";
import { getBridge } from "./bridge";
import { bridgeOperationTracker } from "./bridgeOperationTracker";
import { emitPaymentOutcome } from "./paymentOutcomeBus";
import { MessageType } from "./types";
import type { PaymentPurchaseSuccessData } from "./types";

/** 原生 `bridge-get-pending-outcomes` 返回的单条未送达支付结果。 */
interface PendingBridgeOutcome {
  operationId: string;
  type: string;
  state: "pending" | "completed" | "failed" | "cancelled";
  message?: {
    type?: string;
    data?: Record<string, unknown>;
  };
}

let reconcileInFlight: Promise<void> | null = null;
let lastReconcileAt = 0;
const RECONCILE_DEBOUNCE_MS = 250;

/**
 * 防抖调度支付 reconcile（250ms）。
 * 在 `shell-webview-interactive`、`visibilitychange`、`app-foreground` 等时机调用，
 * 避免原生弹层关闭瞬间多次触发。
 */
export function schedulePaymentBridgeReconcile(reason?: string): void {
  if (typeof window === "undefined") {
    return;
  }
  const now = Date.now();
  if (now - lastReconcileAt < RECONCILE_DEBOUNCE_MS) {
    return;
  }
  lastReconcileAt = now;
  window.setTimeout(() => {
    void reconcilePaymentBridgeSessions(reason);
  }, RECONCILE_DEBOUNCE_MS);
}

/**
 * 对进行中的支付会话执行完整 reconcile 流程：
 * 1. 向原生拉取未送达的 outcome 并重放
 * 2. 仍未决则查 `payment-get-status`
 * 3. 超时后标记取消并通知 UI
 */
async function reconcilePaymentBridgeSessions(_reason?: string): Promise<void> {
  const bridge = getBridge();
  if (!bridge.isInApp()) {
    return;
  }

  const inFlight = bridgeOperationTracker.getInFlightPaymentSessions();
  if (inFlight.length === 0) {
    return;
  }

  if (reconcileInFlight) {
    await reconcileInFlight;
    return;
  }

  reconcileInFlight = (async () => {
    inFlight.forEach((s) => {
      bridgeOperationTracker.updatePaymentSession(s.operationId, {
        state: "reconciling",
      });
    });

    try {
      const outcomes = await fetchPendingPaymentOutcomes();
      for (const outcome of outcomes) {
        dispatchPendingOutcome(outcome);
        if (outcome.operationId) {
          bridge.send(MessageType.BRIDGE_ACK_OUTCOME, {
            operationId: outcome.operationId,
          });
        }
      }
    } catch (err) {
      Track.log(
        "warn",
        "paymentBridgeReconcile",
        "get-pending-outcomes failed",
        { cause: String(err) },
      );
    }

    const stillInFlight = bridgeOperationTracker.getInFlightPaymentSessions();
    for (const session of stillInFlight) {
      if (Date.now() - session.startedAt < 4000) {
        // 刚发起购买，避免 sheet 未关时误判为取消
        continue;
      }
      const recovered = await tryRecoverViaPaymentStatus(session);
      if (recovered) {
        continue;
      }
      if (Date.now() - session.startedAt < 120_000) {
        // 2 分钟内仍可能完成，保持 reconciling
        continue;
      }
      bridgeOperationTracker.updatePaymentSession(session.operationId, {
        state: "cancelled",
      });
      bridgeOperationTracker.clearPaymentSession(session.operationId);
      emitPaymentOutcome({
        kind: "result",
        data: {
          success: false,
          error: {
            code: "BILLING_RECONCILE_NO_PURCHASE",
            errMsg: "No purchase outcome",
          },
        },
        operationId: session.operationId,
        source: "reconcile-fallback",
      });
    }
  })().finally(() => {
    reconcileInFlight = null;
  });

  await reconcileInFlight;
}

/**
 * 调用 `bridge-get-pending-outcomes` 拉取原生 Registry/Outbox 中未 ack 的支付结果。
 * 旧版原生不支持时返回空数组，由后续 `getStatus` 兜底。
 */
async function fetchPendingPaymentOutcomes(): Promise<PendingBridgeOutcome[]> {
  const bridge = getBridge();
  const sinceMs = Date.now() - 30 * 60 * 1000;
  try {
    const response = await bridge.request(
      MessageType.BRIDGE_GET_PENDING_OUTCOMES,
      {
        types: ["payment-purchase"],
        sinceMs,
      },
      { timeoutMs: 8000 },
    );
    if (!response.success) {
      return [];
    }
    const outcomes = (response.data as { outcomes?: PendingBridgeOutcome[] })
      ?.outcomes;
    return Array.isArray(outcomes) ? outcomes : [];
  } catch {
    return [];
  }
}

/**
 * 当 Outbox 重放无果时，查询商店侧订阅状态是否已包含目标 productId。
 * 命中则按成功处理（可能无 transactionId，confirm 由后续流程或 webhook 兜底）。
 */
async function tryRecoverViaPaymentStatus(
  session: import("./bridgeOperationTracker").PaymentOperationSession,
): Promise<boolean> {
  const bridge = getBridge();
  try {
    const response = await bridge.request(
      MessageType.PAYMENT_GET_STATUS,
      {
        productType: session.productType ?? "SUBS",
      },
      { timeoutMs: 8000 },
    );
    if (!response.success || !response.data) {
      return false;
    }
    const products = (response.data as { products?: string[] }).products ?? [];
    const hasProduct = products.includes(session.storeProductId);
    if (!hasProduct) {
      return false;
    }
    emitPaymentOutcome({
      kind: "success",
      data: {
        productId: session.storeProductId,
      },
      operationId: session.operationId,
      source: "reconcile-fallback",
    });
    bridgeOperationTracker.clearPaymentSession(session.operationId);
    return true;
  } catch {
    return false;
  }
}

/** 将原生 outcome 原始 payload 窄化为 {@link PaymentPurchaseSuccessData}。 */
function asPaymentSuccessData(
  raw: Record<string, unknown> | undefined,
): PaymentPurchaseSuccessData {
  const data = raw ?? {};
  return {
    productId: typeof data.productId === "string" ? data.productId : "",
    transactionId:
      typeof data.transactionId === "string" ? data.transactionId : undefined,
    purchaseToken:
      typeof data.purchaseToken === "string" ? data.purchaseToken : undefined,
    obfuscatedAccountId:
      typeof data.obfuscatedAccountId === "string"
        ? data.obfuscatedAccountId
        : undefined,
  };
}

/** 将原生 outcome 原始 payload 窄化为失败/取消结果结构。 */
function asPaymentResultData(raw: Record<string, unknown> | undefined): {
  success?: boolean;
  error?: unknown;
  productId?: string;
} {
  const data = raw ?? {};
  return {
    success: typeof data.success === "boolean" ? data.success : undefined,
    error: data.error,
    productId: typeof data.productId === "string" ? data.productId : undefined,
  };
}

/**
 * 将单条 pending outcome 转为 `emitPaymentOutcome` 事件并清理会话。
 * 处理完成后由调用方发送 `bridge-ack-outcome` 通知原生。
 */
function dispatchPendingOutcome(outcome: PendingBridgeOutcome): void {
  const message = outcome.message;
  if (!message?.type) {
    return;
  }
  if (message.type === MessageType.PAYMENT_PURCHASE_SUCCESS) {
    emitPaymentOutcome({
      kind: "success",
      data: asPaymentSuccessData(message.data),
      operationId: outcome.operationId,
      source: "reconcile-replay",
    });
    bridgeOperationTracker.clearPaymentSession(outcome.operationId);
    return;
  }
  if (message.type === MessageType.PAYMENT_PURCHASE_RESULT) {
    emitPaymentOutcome({
      kind: "result",
      data: asPaymentResultData(message.data),
      operationId: outcome.operationId,
      source: "reconcile-replay",
    });
    bridgeOperationTracker.clearPaymentSession(outcome.operationId);
  }
}
